/**
 * 이상한 방 - 매치의 사격 · 탄창 · 관전 전환
 *
 * 근거 문서: `이상한방-컨셉시트-v0.1.md` 4.3절(탄환 경제 - 잔탄 0은 관전, 관전자는 남의 화면을 본다) · 4.5절(점수)
 *            `이상한방-상태모델-v0.1.md` 6절(판정) · 12절(동시 클릭 - 늦은 쪽은 탄환 미소모, 오답이 아니므로 벌하지 않는다)
 *            로드맵 모델 2.6절(동시 클릭 · 관전)
 *
 * ## 이 파일에는 탄환 값·배수·거부 사유의 비교식이 없다
 *
 * 판정은 `hunt.resolveShot`(공유 `found`를 본다), 탄환 비용은 `hunt.ammoCostForOutcome`, 콤보는 `combo.comboAfter`,
 * 사격 게이트는 `hunt.isShotBlocked`, 탄창 산술은 `gameState.magazineUsesLeft`·`hasRoomForFullMagazine`·`refilledAmmo`,
 * 저탄 임계는 `gameState.crossesLowAmmoThreshold`, 걸기 조건은 `raise.canRaise`를 **그대로 호출**한다.
 * 걸기(M7)가 이 파일에 있는 이유는 그것이 술래의 것이기 때문이다 - 선언 뒤 빗나감의 값(`ammoCostForOutcome`의 `raised`)과
 * 발견 기록의 `raised`가 여기서 굳고, 배수 자체는 `hunt.ts`·`raise.ts`가 매긴다. 이 파일에 `× 2`도 `>= 4`도 없다.
 * 싱글과 다른 것은 `found`의 각 줄에 `by`가 붙는 것과, 잔탄·탄창이 함께 바닥난 사람이 라운드를 끝내는 대신
 * 관전으로 넘어가는 것 둘뿐이다. `matchParity.test.ts`가 두 리듀서의 `ammo`·`found.length`·`combo`·`score` 열이
 * 같음을 CI에서 지킨다 - 이 파일에 규칙을 한 줄 적는 순간 거기서 터진다.
 *
 * ## 순서 규칙 - `gameState.onMiss`를 그대로 인용한다
 *
 * > **잔탄 0이 곧 라운드의 끝이던 규칙이 여기서 뒤집힌다.** 라운드 종료 판정보다 **탄창 검사를 먼저** 한다.
 * > 순서가 규칙이다 - 종료를 먼저 판정하면 잔탄 0인 순간 페이즈가 리빌로 넘어가고, `canUseMagazine`의
 * > 첫 조건(`phase === 'hunt'`)이 거짓이 되어 **탄창이 있어도 영원히 못 쓴다.** 잔탄 0에서 쓸 수 있다는 것이
 * > 이 자원의 존재 이유이므로, 그 순간을 막는 코드는 자원 자체를 없애는 것과 같다.
 *
 * 매치에서는 "라운드 종료" 자리에 "관전 전환"이 온다. 잔탄 0이 되면 **탄창 검사 먼저**, 쓸 수 있으면 재장전
 * 안내(`lowAmmo`)만 내고 좌석은 그대로, 못 쓰면 그때 `spectating: true`다. 관전으로 넘어간 사람은
 * `shoot`·`requestOrigin`·`useMagazine`이 참조 동일이고(관전자는 어떤 이벤트로도 상태를 못 바꾼다),
 * 원래 방에 있었다면 문을 비우고 **줄·예약에 있었다면 거기서도 빠진다** - 관전자는 아무 이벤트도 못 보내므로
 * 줄에 남으면 문이 그 사람에게 예약된 채 유예가 흐르고 뒤에 선 사람은 `occupied`만 받는다. 이 사람을 따라가던
 * 관전자들의 `spectateTarget`도 함께 풀린다(관전자를 따라가면 빈 화면이다). 술래 전원이 관전이면 `all-out`으로 리빌이다.
 *
 * ## 내 구획의 이상한 곳은 못 쏜다 (M11 규칙 4)
 *
 * 그것은 내가 만든 것이라 찾을 것이 없다. 판정이 `hit`을 냈는데 그 변화의 주인이 나면 `own-zone`으로 되돌린다 -
 * **탄환 0, 콤보 불변**이라 `already-found`와 정확히 같은 취급이다(손해를 주면 "실수로 내 방을 눌렀다"가 벌이 된다).
 * 판정기(`hunt.resolveShot`)에 넣지 않은 이유는 싱글에 구획 주인이 없어서다 - 넣으면 싱글이 쓰지 않는 갈래가
 * 판정기에 생기고 패리티 테스트가 그 갈래를 영영 못 밟는다.
 *
 * ## 동시 클릭은 새 규칙이 아니다
 *
 * 호스트 큐가 도착 순으로 부르므로 "동시"가 없다. 첫 발이 `found`에 굳고, 둘째 발은 `resolveShot`이 공유 `found`를 보고
 * `already-found` → 비용 0, 콤보 유지. 다른 점은 피드백 하나 - 창 안이면 `sniped{by, gapMs}`. 조용히 씹으면 버튼
 * 고장으로 읽힌다.
 */

import type { PlayerId } from '../../core/types'
import { MS_PER_SECOND } from '../../core/time'
import { breaksCombo, comboAfter, comboMultiplier, isPerfectRun, type ComboOutcome } from '../combo'
import { crossesLowAmmoThreshold, hasRoomForFullMagazine, magazineUsesLeft, refilledAmmo } from '../gameState'
import { ammoAfterMiss, ammoCostForOutcome, isShotBlocked as isShotBlockedBy, type HitTarget } from '../hunt'
import { canRaise as canRaiseWith } from '../raise'
import type { Tuning } from '../tuning'
import { dropFromQueue, releaseDoor } from './matchOrigin'
import { settleHunt } from './matchPhases'
import {
  chain,
  findableChanges,
  findableRemaining,
  idle,
  mapPlayers,
  patchPlayer,
  playerOf,
  seatsInOrder,
  withPlayer,
} from './matchSelectors'
import type { MatchChange, MatchDeps, MatchEffect, MatchFound, MatchPlayer, MatchState, MatchStep } from './matchTypes'

/* --- 게이트 ---------------------------------------------------------------------- */

/**
 * 이 좌석이 지금 찾는 사람으로 행동할 수 있는가(수색 중 · 관전 아님). 거짓이면 이벤트는 **참조 동일**로 무시된다 -
 * 수색 밖에는 사격 UI가 없고 관전자에게는 버튼 자체가 없다(`hunt.ts` 머리말: 매치는 자기 게이트를 따로 갖는다).
 * M11에서 역할 조건이 빠졌다 - 전원이 자기 구획을 고치고 **전원이 남의 구획을 찾는다**.
 */
export function isActiveHunter(state: MatchState, player: MatchPlayer | undefined): player is MatchPlayer {
  return state.phase === 'hunt' && player !== undefined && !player.spectating
}

/**
 * 활동 중인 술래인데 지금 한 발이 **안 나가는** 이유가 있는가(원래 방 안 · 잔탄 0). 비교식은 `hunt.isShotBlocked`의
 * 것이고 여기는 좌석의 두 필드를 그 모양으로 넘길 뿐이다 - 싱글의 `canShoot`과 같은 함수라 두 리듀서의 게이트가
 * 갈릴 수 없다. `onShoot`은 이것이 참이면 `blocked`로 알리고, `matchView.canShoot`은 거짓이 된다.
 */
export function isShotBlocked(player: MatchPlayer): boolean {
  return isShotBlockedBy({ isInOriginRoom: player.origin.isInOriginRoom, ammo: player.ammo })
}

/**
 * 탄창을 쓸 수 있는가. 로드맵이 문장으로 굳힌 조건 그대로다: `hasRoomForFullMagazine && magazineUsesLeft > 0`.
 * `gameState.canUseMagazine`은 `phase`를 읽어 좌석에 못 쓰므로, 그 안의 두 리프를 같은 순서로 부른다.
 * `matchView`의 `canUseMagazine` 불리언도 이것이다.
 */
export function canReload(player: MatchPlayer, tuning: Tuning): boolean {
  if (!hasRoomForFullMagazine(player, tuning)) return false
  return magazineUsesLeft(player, tuning) > 0
}

/**
 * 지금 걸 수 있는가(잔탄 · 라운드 1회). 비교식은 `raise.canRaise`의 것이고 여기는 좌석의 두 필드를 넘길 뿐이다 -
 * 싱글의 `gameState.canRaiseNow`와 같은 함수라 두 리듀서의 조건이 갈릴 수 없다. `matchView.canRaise`도 이것이다.
 */
export function canRaise(player: MatchPlayer, tuning: Tuning): boolean {
  return canRaiseWith(player.ammo, player.raised, tuning)
}

function blocked(state: MatchState, id: PlayerId, targetId: string | null): MatchStep {
  return {
    state,
    effects: [
      { to: id, body: { type: 'shotResolved', kind: 'blocked', targetId } },
      { to: id, body: { type: 'playSound', id: 'shot-blocked' } },
    ],
  }
}

/* --- 콤보 ------------------------------------------------------------------------ */

/** `gameState.withComboOutcome`과 같은 모양. 정답 경로와 갈라 둔 이유도 같다 - 중복 사격이 콤보를 끊지 않는다는 규칙이 조건문에 숨지 않게. */
function withComboOutcome(state: MatchState, player: MatchPlayer, outcome: ComboOutcome): MatchStep {
  const before = player.combo
  const combo = comboAfter(before, outcome)
  if (!breaksCombo(before, outcome)) {
    return combo === before ? idle(state) : { state: patchPlayer(state, player.id, { combo }), effects: [] }
  }
  return {
    state: patchPlayer(state, player.id, { combo }),
    effects: [{ to: player.id, body: { type: 'comboBroken', at: before } }],
  }
}

/* --- 세 결과 ---------------------------------------------------------------------- */

function onHit(state: MatchState, player: MatchPlayer, change: MatchChange): MatchStep {
  const combo = comboAfter(player.combo, 'hit')
  // 걸기(M7)도 콤보처럼 이 순간의 값으로 굳는다(`gameState.onHit`과 같은 모양 - 걸지 않았으면 필드를 남기지 않는다).
  const entry: MatchFound = {
    changeId: change.id,
    by: player.id,
    owner: change.owner,
    foundAtMs: state.phaseElapsedMs,
    combo,
    ...(player.raised ? { raised: true } : {}),
  }
  const found: readonly MatchFound[] = [...state.found, entry]

  const next: MatchState = {
    ...withPlayer(state, {
      ...player,
      combo,
      maxCombo: Math.max(player.maxCombo, combo),
      /*
       * 퍼펙트는 이 순간에만 판정할 수 있다(`gameState.onHit`). 기준은 **내가 찾을 수 있는 수**다(M11) -
       * 전체를 기준으로 삼으면 내 구획의 것을 내가 찾을 수 없으므로 아무도 영원히 퍼펙트가 아니다.
       */
      hasPerfect: isPerfectRun(combo, findableChanges(state, player.id).length),
    }),
    found,
  }
  const effects: MatchEffect[] = [
    // 소리와 자막이 콤보를 읽어야 하므로 발견 통지보다 먼저 낸다.
    { to: player.id, body: { type: 'comboUp', combo, multiplier: comboMultiplier(combo) } },
    { to: player.id, body: { type: 'shotResolved', kind: 'hit', targetId: change.targetId } },
    { to: player.id, body: { type: 'playSound', id: 'shot-hit' } },
    /*
     * 발견 통지는 **받는 사람마다 하나씩**이다(M11). "N개 남았다"의 N이 사람마다 다르기 때문이다 -
     * 내 구획의 것은 내가 못 찾으므로 전원 공통의 남은 개수라는 것이 존재하지 않는다. 나머지 필드는 전원 같다.
     */
    ...seatsInOrder(next).map((seat): MatchEffect => ({
      to: seat.id,
      body: {
        type: 'foundChange',
        by: player.id,
        changeId: change.id,
        targetId: change.targetId,
        remaining: findableRemaining(next, seat.id),
      },
    })),
  ]
  return chain({ state: next, effects }, settleHunt)
}

/**
 * 내 구획의 이상한 곳을 쐈다(M11 규칙 4). `already-found`와 같은 취급 - 탄환도 콤보도 그대로이고 표식만 다르다.
 * 콤보를 명시적으로 `already-found`로 통과시키는 이유는 그것이 "끊지 않는다"의 유일한 표현이기 때문이다
 * (`combo.breaksCombo`) - 여기서 콤보를 건드리지 않고 지나가면 규칙이 조건문에 숨는다.
 */
function onOwnZone(state: MatchState, player: MatchPlayer, change: MatchChange): MatchStep {
  const kept = withComboOutcome(state, player, 'already-found')
  return {
    state: kept.state,
    effects: [
      ...kept.effects,
      { to: player.id, body: { type: 'shotResolved', kind: 'own-zone', targetId: change.targetId } },
      { to: player.id, body: { type: 'playSound', id: 'shot-blocked' } },
    ],
  }
}

/** 이미 찾은 것을 쐈다. 탄환도 콤보도 그대로. 남이 막 찾은 것이면 "OO이 N초 먼저"를 덧붙인다. */
function onAlreadyFound(state: MatchState, player: MatchPlayer, hit: HitTarget, tuning: Tuning): MatchStep {
  const kept = withComboOutcome(state, player, 'already-found')
  const effects: MatchEffect[] = [
    ...kept.effects,
    { to: player.id, body: { type: 'shotResolved', kind: 'already-found', targetId: hit.instanceId } },
    { to: player.id, body: { type: 'playSound', id: 'shot-blocked' } },
  ]

  const change = state.changes.find((candidate) => candidate.targetId === hit.instanceId)
  const entry = change === undefined ? undefined : state.found.find((found) => found.changeId === change.id)
  if (entry !== undefined && entry.by !== player.id) {
    const gapMs = state.phaseElapsedMs - entry.foundAtMs
    if (gapMs <= tuning.snipedWindowSeconds * MS_PER_SECOND) {
      effects.push({ to: player.id, body: { type: 'sniped', by: entry.by, gapMs } })
    }
  }
  return { state: kept.state, effects }
}

/**
 * 오답. 콤보를 먼저 끊고(잔탄 0으로 관전이 되는 경로에서도 `comboBroken`이 빠지지 않게) 탄환을 깎는다.
 * @param ammoCost `hunt.ts`가 정한 값. 여기서 1을 적으면 탄환 규칙이 두 곳에 생긴다.
 */
function onMiss(state: MatchState, player: MatchPlayer, ammoCost: number, tuning: Tuning): MatchStep {
  const broken = withComboOutcome(state, player, 'miss')
  // 0에서 멈추는 것은 `hunt.ammoAfterMiss`가 정한다(`gameState.onMiss`와 같은 함수) - 음수 잔탄은 탄창이 한 발을 잃는다.
  const ammo = ammoAfterMiss(player.ammo, ammoCost)
  const drained: MatchState = patchPlayer(broken.state, player.id, { ammo })
  const effects: MatchEffect[] = [
    ...broken.effects,
    { to: player.id, body: { type: 'shotResolved', kind: 'miss', targetId: null } },
    { to: player.id, body: { type: 'playSound', id: 'shot-miss' } },
  ]

  // 임계선을 넘는 순간에만 울린다(`gameState.onMiss`와 같은 함수). 매 오답마다 울리면 경고가 배경음이 된다.
  if (crossesLowAmmoThreshold(player.ammo, ammo, tuning)) {
    effects.push(
      { to: player.id, body: { type: 'lowAmmo', ammo } },
      { to: player.id, body: { type: 'playSound', id: 'ammo-low' } },
    )
  }
  if (ammo > 0) return { state: drained, effects }

  // **탄창 검사 먼저**(머리말 인용). 쓸 수 있으면 관전이 아니라 "재장전하라"다.
  const emptied = playerOf(drained, player.id)
  if (emptied !== undefined && canReload(emptied, tuning)) {
    effects.push(
      { to: player.id, body: { type: 'lowAmmo', ammo } },
      { to: player.id, body: { type: 'playSound', id: 'ammo-low' } },
    )
    return { state: drained, effects }
  }

  return chain({ state: drained, effects }, (current) => becomeSpectator(current, player.id, tuning))
}

/**
 * 관전으로 넘어간다(4.3절). 문과 얽힌 자리를 전부 정리한다 - 원래 방에 있었다면 방문 상태를 지우고 문을 비우고
 * (`releaseDoor`), 줄이나 예약에 있었다면 거기서 빠진다(`dropFromQueue` - `leaveQueue`와 같은 경로). 그래야 줄 선
 * 사람이 들어간다. 쿨타임은 남기지만 관전자는 다시 못 들어가므로 뜻은 없다.
 * 이 사람을 따라가던 관전자들은 대상이 풀린다 - `onSpectate`가 관전자를 대상으로 허용하지 않는 것과 같은 규칙이고,
 * 그 사람들에게 `spectating{target: null}`을 다시 낸다. 술래 전원이 관전이면 `settleHunt`가 `all-out`으로 끝낸다.
 */
function becomeSpectator(state: MatchState, id: PlayerId, tuning: Tuning): MatchStep {
  const player = playerOf(state, id)
  if (player === undefined) return idle(state)
  const followers = Object.values(state.players).filter((other) => other.spectateTarget === id)
  const seated: MatchState = mapPlayers(state, (seat) => {
    if (seat.id === id) {
      return {
        ...seat,
        spectating: true,
        spectateTarget: null,
        origin: { ...seat.origin, isInOriginRoom: false, originStayElapsedMs: 0 },
      }
    }
    return seat.spectateTarget === id ? { ...seat, spectateTarget: null } : seat
  })
  const effects: MatchEffect[] = [
    { to: 'all', body: { type: 'spectating', playerId: id, target: null } },
    { to: id, body: { type: 'playSound', id: 'spectate-enter' } },
    ...followers.map((other): MatchEffect => ({ to: 'all', body: { type: 'spectating', playerId: other.id, target: null } })),
  ]
  if (state.door.holder === id) effects.push({ to: id, body: { type: 'warpTo', origin: false } })

  // 줄과 문은 겹치지 않는다(입장하면 줄에서 빠진다) - 그래도 둘을 다 지나는 것이 "경로는 하나"다.
  const unqueued = dropFromQueue(seated, id, tuning)
  const released = state.door.holder === id ? releaseDoor(unqueued.state, tuning) : idle(unqueued.state)
  return chain(
    { state: released.state, effects: [...effects, ...unqueued.effects, ...released.effects] },
    settleHunt,
  )
}

/* --- 이벤트 ---------------------------------------------------------------------- */

/**
 * 사격. 게이트 → 판정(`deps.resolveShot`, 공유 `found` 투영) → 세 결과.
 * 원래 방 안과 잔탄 0은 "눌렀는데 안 나간" 상황이라 `blocked`로 알린다(4.4절: 원래 방은 비교하는 곳이지 쏘는 곳이 아니다).
 */
export function onShoot(state: MatchState, id: PlayerId, hit: HitTarget | null, deps: MatchDeps): MatchStep {
  const player = playerOf(state, id)
  if (!isActiveHunter(state, player)) return idle(state)
  if (isShotBlocked(player)) return blocked(state, id, hit?.instanceId ?? null)

  const outcome = deps.resolveShot({ changes: state.changes, found: state.found, mutated: state.mutated }, hit)
  switch (outcome.kind) {
    case 'hit': {
      // 판정기는 주인을 모른다(싱글과 같은 함수다) - 주인은 정답표의 줄에 있다(`MatchChange.owner`).
      const change = state.changes.find((candidate) => candidate.id === outcome.change.id)
      if (change === undefined) return idle(state)
      return change.owner === id ? onOwnZone(state, player, change) : onHit(state, player, change)
    }
    case 'already-found':
      // `resolveShot`이 `null`을 `miss`로 답하므로 여기서 `hit`은 있다. 타입이 그 사실을 모르니 방어만 한다.
      return hit === null ? idle(state) : onAlreadyFound(state, player, hit, deps.tuning)
    case 'miss':
      // 걸기 뒤의 빗나감은 비싸다. 얼마나인지는 `hunt.ts`가 정한다(`gameState.onShoot`과 같은 호출).
      return onMiss(state, player, ammoCostForOutcome(outcome, player.raised, deps.tuning), deps.tuning)
  }
}

/**
 * 걸기(M7). 활동 중인 술래만(아니면 참조 동일 - 버튼 자체가 없다). 못 거는 상황(잔탄 부족 · 이미 걸었다)은 "눌렀는데 안 됐다"라
 * `shot-blocked` 소리로 알리되 `shotResolved{blocked}`는 내지 않는다 - 그 효과는 탭 한 발과 짝지어진다(`onUseMagazine`과 같은 이유).
 * 성립하면 **전원에게** `raised{by}` - "OO이 걸었다!" 배너의 재료다(로드맵 3.5절). 값어치는 다음 사격부터 `hunt.ts`가 매긴다.
 */
export function onRaise(state: MatchState, id: PlayerId, deps: MatchDeps): MatchStep {
  const player = playerOf(state, id)
  if (!isActiveHunter(state, player)) return idle(state)
  if (!canRaise(player, deps.tuning)) {
    return { state, effects: [{ to: id, body: { type: 'playSound', id: 'shot-blocked' } }] }
  }
  return {
    state: patchPlayer(state, id, { raised: true }),
    effects: [{ to: 'all', body: { type: 'raised', by: id } }],
  }
}

/**
 * 탄창 하나를 쓴다. 잔탄 0에서도 통한다 - 그게 이 자원의 존재 이유다. 거부는 조용히 씹지 않는다(`gameState.onUseMagazine`) -
 * 다만 **소리만** 낸다. `shotResolved{blocked}`를 내지 않는 이유는 그 효과가 클라이언트에서 탭 한 발과 짝지어지기
 * 때문이다(`matchSessionEffects.onShotResolved` - 도착 순 FIFO). 탄창 거부가 그 줄에 끼면 뒤따르는 진짜 판정이 자리를 잃는다.
 * 싱글도 같은 거부에 `playSound` 하나다. 채우는 양은 `gameState.refilledAmmo` 하나가 정한다 - 싱글과 같은 함수다.
 */
export function onUseMagazine(state: MatchState, id: PlayerId, deps: MatchDeps): MatchStep {
  const player = playerOf(state, id)
  if (!isActiveHunter(state, player)) return idle(state)
  if (!canReload(player, deps.tuning)) {
    return { state, effects: [{ to: id, body: { type: 'playSound', id: 'shot-blocked' } }] }
  }

  const ammo = refilledAmmo(player.ammo, deps.tuning)
  const magazines = player.magazines - 1
  return {
    state: withPlayer(state, { ...player, ammo, magazines, magazinesUsed: player.magazinesUsed + 1 }),
    effects: [
      { to: id, body: { type: 'magazineUsed', ammo, remaining: magazines } },
      { to: id, body: { type: 'playSound', id: 'ammo-refill' } },
    ],
  }
}

/**
 * `id`가 `target`을 관전 대상으로 고를 수 있는가 - 관전자만, 대상은 관전 중이 아닌 술래(관전자를 따라가면 빈 화면을 본다),
 * 자기 자신은 아니다. `onSpectate`(실제 판정)와 뷰(`matchView.PublicPlayer.spectatable` - 시점 버튼의 얼굴)가 **같은 함수**를
 * 부른다(M5 규칙 2). 조건을 두 곳에 각자 적으면 버튼은 서는데 눌러도 참조 동일인 날이 온다(`canEnterOrigin`과 같은 규약).
 */
export function canSpectate(state: MatchState, id: PlayerId, target: PlayerId): boolean {
  const player = playerOf(state, id)
  if (player === undefined || !player.spectating || target === id) return false
  return isActiveHunter(state, playerOf(state, target))
}

/**
 * 관전 대상을 고른다. 조건은 `canSpectate` 하나다(`null`은 "아무도 안 따라간다"라 관전자면 언제나 된다).
 * 조건에 안 맞으면 참조 동일 - 카메라 추종은 클라이언트의 일이고 규칙이 아니다.
 */
export function onSpectate(state: MatchState, id: PlayerId, target: PlayerId | null): MatchStep {
  const player = playerOf(state, id)
  if (player === undefined || !player.spectating) return idle(state)
  if (target !== null && !canSpectate(state, id, target)) return idle(state)
  if (player.spectateTarget === target) return idle(state)
  return {
    state: patchPlayer(state, id, { spectateTarget: target }),
    effects: [{ to: 'all', body: { type: 'spectating', playerId: id, target } }],
  }
}
